/*
 * 戒导航 · 媒体存储模块（jiedh/media）前台入口
 * =============================================================================
 * 运行范围：Flarum SPA 页面（论坛 /all、博客 /blog 等）。
 *
 * 本文件只做两件事，且都**不修改 vendor/**（全部走 Flarum 公开扩展点）：
 *
 *   1) 覆盖 fof/blog「文章设置」弹窗的头图字段
 *      —— 移除原来的「URL 输入框」，改为「仅上传」：选本地图片 → 传 R2（blog-covers/）→ 回填。
 *         理由：外链图片会过期/防盗链，失效后文章头图变破图，体验很差；
 *               统一存自己的 R2 才能在桶内按前缀长期分类管理。
 *
 *   2) 全局破图兜底
 *      —— 任何 <img> 加载失败（含帖子正文里的外链图片）自动替换为内联占位图。
 *         这是对「历史上已经写进数据库的外链图片」的最后一道保险：
 *         存量外链无法保证长期有效，至少让访客看到的是占位图而不是浏览器破图图标。
 *
 *   3) 移除博客侧栏的「论坛导航」面板（列表页 /blog + 文章页 /blog/xxx）。
 *      2026-09-24 用户裁决：博客侧栏只需「分类目录」，论坛导航冗余（详见
 *      doc/博客布局与论坛一致性优化设计方案.md 变更记录 v0.3）。
 *
 *   4) 博客列表「没有头图就不显示图」（2026-10-21 用户裁决）。
 *      列表普通卡靠 CSS 即可（无头图必带 .FoFBlog-default-image 类）；
 *      精选大卡的该类名是 vendor **无条件硬编码**的，只能在这里补标记类，
 *      具体样式见 less/forum.less 第四块。
 *
 *   5) 文章详情页「没有头图就不显示默认头图」（2026-10-25 用户裁决）。
 *      vendor 会无条件渲染头图容器并用站点默认图兜底，这里在无真实头图时
 *      直接把 'image' 这一项从 ItemList 里移除，具体见下文第五块。
 *
 *   6) 博客列表卡「显示分类标签」（2026-10-27 用户要求）。
 *      求索等来源导入的文章在库里已完成归类（每篇挂 1~3 个主题次标签 + 可选「精华」），
 *      但 fof/blog 的普通列表卡只把标签拼进 className（无任何样式、不输出文字），
 *      前台看上去仍是一堆没分类的文章。这里在卡片内容区补一排可点击的分类芯片，
 *      点一下直达 /blog/category/<slug>，具体见下文第六块。
 *
 *   7) 侧栏「分类目录」高亮当前分类（2026-10-27 用户要求）。
 *      进入 /blog/category/<slug> 后，侧栏里对应的那一项要明显区别于其它项，
 *      让用户知道自己正在哪个分类。vendor 不输出任何 active 状态，这里补标记类，
 *      样式用「该分类自己的颜色」做浅底 + 左侧色条，具体见下文第七块与 less 第九块。
 *
 * 硬约束：flarum-webpack-config 在 js/ 根目录探测 `forum.ts` 作为 webpack 入口
 * （见 packages/media/js/forum.ts 的说明），真正的逻辑放在本文件。
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import BlogCoverUploader from './components/BlogCoverUploader';

/**
 * 目标：fof/blog「文章设置」弹窗。
 *
 * 用**字符串路径**而不是直接 import，原因见 Flarum 的惰性模块机制：
 *   `extend('ext:命名空间/模块路径', ...)` 会被解析为 flarum.reg.onLoad(namespace, id, ...)，
 *   若模块尚未加载则挂起，等它注册时再执行 —— 因此与扩展的加载顺序无关，
 *   也无需把 fof/blog 的前端打进我们自己的 bundle。
 *   命名空间由 `fof/blog` 推导为 `fof-blog`（ExportRegistry::namespaceAndIdFromPath）。
 */
const BLOG_POST_SETTINGS_MODAL = 'ext:fof/blog/forum/components/Modals/BlogPostSettingsModal';

/**
 * 破图占位图（内联 SVG data URI，零额外请求、不占用 R2 存储）。
 *
 * 画面：浅灰底 + 简化「图片」图标，风格中性，与主站浅色主题一致。
 * 用 encodeURIComponent 编码，避免 SVG 里的 `#`、`<`、引号破坏 data URI。
 */
const BROKEN_IMAGE_PLACEHOLDER = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360" viewBox="0 0 480 360">' + '<rect width="480" height="360" fill="#eef1f4"/>' + '<g fill="none" stroke="#b9c2cc" stroke-width="12" stroke-linecap="round" stroke-linejoin="round">' + '<rect x="96" y="108" width="288" height="144" rx="12"/>' + '<path d="M120 228l60-66 48 54 44-40 88 52"/>' + '</g>' + '<circle cx="330" cy="150" r="18" fill="#b9c2cc"/>' + '</svg>');

/**
 * 一、把「文章设置」弹窗的头图字段换成「仅上传」组件。
 *
 * 实现方式：扩展弹窗的 `fields()` 方法（返回 ItemList），在它产出结果后把 'image' 这一项的
 * 内容整体替换掉。
 *   · 必须先 `items.has('image')`：ItemList::setContent() 对不存在的 key 会直接抛错；
 *   · 只改这一项，其余字段（摘要、精选、保存按钮等）保持 fof/blog 原样；
 *   · 头图的值仍然写在弹窗的 featuredImage Stream 上，所以「保存」链路完全复用官方实现。
 */
function useUploadOnlyBlogCover() {
  extend(BLOG_POST_SETTINGS_MODAL, 'fields', function (items) {
    if (!items.has('image')) return;
    items.setContent('image', m(BlogCoverUploader, {
      host: this
    }));
  });
}

/**
 * 二、全局图片加载失败兜底 —— 统一替换为占位图。
 *
 * 关键点：
 *   · 监听必须注册在**捕获阶段**（第三个参数 true）：`<img>` 的 error 事件不冒泡，
 *     只有捕获阶段才能在 document 上收到；
 *   · 用 src 记录避免死循环：占位图自身若也加载失败则不再替换；
 *     同一元素换了新地址后再次失败，仍能再次兜底；
 *   · 不改变任何业务数据：只改 DOM 上这一处显示，数据库里的地址原样保留，
 *     方便后续排查到底是哪张图失效（失败的原地址记在 data-jiedh-fallback-src 上）。
 */
function useBrokenImageFallback() {
  document.addEventListener('error', event => {
    const target = event.target;

    // 只处理图片（<script> / <link> 等资源错误同在捕获阶段，需排除）
    if (!(target instanceof HTMLImageElement)) return;
    const failedSrc = target.src;

    // 占位图自身也失败 → 直接放弃，避免无限循环
    if (failedSrc === BROKEN_IMAGE_PLACEHOLDER) return;

    // 同一地址已经兜底过 → 跳过（防止重绘/重复 error 反复改写）
    if (target.dataset.jiedhFallbackSrc === failedSrc) return;

    // 记录「是哪张图失效的」，同时作为上面去重判断的依据
    target.dataset.jiedhFallbackSrc = failedSrc;
    target.src = BROKEN_IMAGE_PLACEHOLDER;
  }, true);
}

/**
 * 三、移除博客侧栏的「论坛导航」面板（列表页 + 文章页）。
 *
 * 背景：fof/blog 会把论坛左栏导航（全部讨论/标签/排行榜等）原样复制一份放进
 * 博客侧栏面板（组件 ForumNav）。对本站而言该入口冗余——顶部导航与左侧分类目录
 * 已覆盖跳转需求，2026-09-24 用户裁决移除。
 *
 * 手法：不使用 CSS display:none（那样 DOM 里仍会渲染一份重复导航链接，
 * 对 SEO / 读屏是噪音），而是走 vendor **官方预留的两个扩展点**，让它根本不渲染：
 *   · 列表页 BlogOverview：oninit 里硬编码 `this.showForumNav = true`（L44），
 *     view 中是 `{this.showForumNav && <ForumNav />}`（L198）——
 *     我们在原 oninit 执行完后把该开关置 false，mithril 对 `false` 不产生任何节点；
 *   · 文章页 BlogItemSidebar：ForumNav 以 key 'nav' 加入 items() 的 ItemList（L31），
 *     extend items() 后 `items.remove('nav')` 即可（ItemList 公开 API，key 不存在时静默无操作）。
 *
 * 两处都用字符串路径 extend（惰性模块机制，与加载顺序无关），
 * 依赖面仅 2 个 vendor 模块路径 + 1 个 ItemList key，升级 fof/blog 时按
 * doc/博客布局与论坛一致性优化设计方案.md §七 回归检查。
 *
 * ⚠ Flarum 2.x extend 语义（与 1.x 不同，踩坑记录 2026-09-24）：
 *   wrapper 会**先自动调用原方法**，再把原方法的「返回值」作为扩展回调的第一个参数传入
 *   （见产物里的包装器：`const c = o ? o.apply(this, u) : void 0; r.apply(this, [c, ...u])`）。
 *   因此回调里绝不能再 `original.call(this, ...)`——第一参数不是可调用的 original，
 *   会抛 TypeError 且被 handleErrorOnce 静默吞掉，表现为「扩展没报错但也没生效」。
 *   正确写法：回调里直接改状态即可（原方法此刻已经执行完）。
 */
function removeBlogForumNav() {
  // 列表页 /blog：关掉 vendor 预留的 showForumNav 开关
  // （原 oninit 由 wrapper 先行执行，其中硬编码的 showForumNav=true 已被我们覆盖为 false）
  extend('ext:fof/blog/forum/pages/BlogOverview', 'oninit', function () {
    this.showForumNav = false;
  });

  // 文章页 /blog/xxx：从侧栏 ItemList 移除 ForumNav 面板
  extend('ext:fof/blog/forum/components/BlogItemSidebar/BlogItemSidebar', 'items', function (items) {
    items.remove('nav');
  });
}

/**
 * 四、博客列表：没有头图就不显示图（2026-10-21 用户裁决）。
 *
 * 问题现象：/blog 列表里凡是没有真实头图的文章，都被 fof/blog 用一棵绿色树木
 *   占位 SVG 填满（vendor/fof/blog/less/Forum/Overview.less 的 `.FoFBlog-default-image`），
 *   与正文风格完全不搭，视觉很乱。
 *
 * 两类卡片的处理方式不同：
 *   · 普通列表卡（BlogOverviewItem）：photo 元素仅在「无真实头图」时才带
 *     `.FoFBlog-default-image` 类（vendor 里是 `blogImage === defaultImage` 判断），
 *     因此纯 CSS 就能精确命中 → 见 less/forum.less 4.1（display:none，文字自动占满整行）。
 *   · 精选大卡（FeaturedBlogItem）：该类名是 **无条件硬编码**在 className 里的
 *     （vendor FeaturedBlogItem.tsx L123），有图没图都一样，CSS 无法区分。
 *     只能在这里给「无头图」的卡片补一个自己的标记类 `.Jiedh-blog-noCover`，
 *     再交给 less/forum.less 4.2 换成浅色文字卡片样式。
 *
 * 实现细节：
 *   · 走 `extend(..., 'view', ...)`：Flarum 2.x 的 extend 语义是「先执行原方法，
 *     再把**返回值**（这里是渲染出的 vnode）作为第一参数交给回调」，
 *     回调对返回值**原地修改**即可生效（见 flarum/common/extend.ts 的注释）。
 *     所以这里直接改 `vnode.attrs.className` / `vnode.attrs.style`，不需要重写 view；
 *   · 顺带把 inline 的 `backgroundImage` 置为 none：若站点配置过 fof/blog 的
 *     默认头图（blogDefaultImageUrl），vendor 会把它的 inline style 写进卡片，
 *     样式表里的 background-image 压不过 inline style，这里一并关掉，
 *     保证「无头图 = 真的没有图」；
 *   · 每次渲染都是新的 vnode，className 不会在多次重绘中累加。
 */
const FEATURED_BLOG_ITEM = 'ext:fof/blog/forum/components/FeaturedBlogItem';

/** 只声明本文件用到的两个方法，避免把 fof/blog 的类型打进我们的 bundle */

function useHideMissingBlogCovers() {
  extend(FEATURED_BLOG_ITEM, 'view', function (vnode) {
    const blogMeta = this.attrs?.article?.blogMeta?.();

    // 有真实头图 → 保持 vendor 原样（树图/大图都正常显示）
    if (blogMeta && blogMeta.featuredImage && blogMeta.featuredImage()) return;
    const attrs = vnode?.attrs;
    if (!attrs) return;

    // 补标记类（less 4.2 依据它把卡片切换成浅色文字卡）
    attrs.className = `${attrs.className || ''} Jiedh-blog-noCover`.trim();

    // 关掉 vendor 写进来的 inline 背景图（含站点默认头图）
    attrs.style = {
      ...(attrs.style || {}),
      backgroundImage: 'none'
    };
  });
}

/**
 * 五、文章详情页：没有头图就不显示默认头图（2026-10-25 用户裁决）。
 *
 * 问题：vendor 的 BlogItem.contentItems() 里，头图容器是**无条件渲染**的，
 *   没有 featuredImage 时用站点默认头图兜底（blogDefaultImageUrl，本站是一张绿色树木
 *   SVG），于是每篇没传头图的文章详情页顶部都顶着同一张占位图，观感很假。
 *
 * 手法：extend contentItems（原方法返回 ItemList），在「文章已加载且无头图」时
 *   `items.remove('image')` —— 与列表页的处理保持同一口径（无图 = 真的没有图）。
 *   · 加载中（this.article 为空）保留骨架图：此时还不知道有没有头图，
 *     提前移除会让首屏出现一次明显的布局跳动；
 *   · 移除后「分类」成了卡片第一行，卡片顶部会贴边，
 *     由 less/forum.less 第七块给首行补内边距。
 *
 * ⚠ Flarum 2.x extend 语义：回调的第一个参数是**原方法的返回值**（不是 original 函数），
 *   直接改这个 ItemList 即可 —— 它随后会被 articleItems() 里的 toArray() 消费。
 */
const BLOG_ITEM = 'ext:fof/blog/forum/pages/BlogItem';
function useHideMissingBlogCoverOnDetail() {
  extend(BLOG_ITEM, 'contentItems', function (items) {
    // 加载中：还不知道有没有头图，保留骨架图避免布局跳动
    if (!this.article) return;
    const blogMeta = this.article.blogMeta?.();

    // 有真实头图 → 保持 vendor 原样
    if (blogMeta && blogMeta.featuredImage && blogMeta.featuredImage()) return;
    items.remove('image');
  });
}

/**
 * 六、博客列表卡：显示分类标签（2026-10-27 用户要求）。
 *
 * 背景（为什么要做）：
 *   求索（qiusuoo）导入的 2,360 篇文章已由 `content:curate` 分门归类
 *   （每篇挂 1~3 个主题次标签，另有 1,996 篇打了「精华」），数据层完全就绪
 *   （详见 doc/内容归类与质量治理设计方案.md §11）。
 *   但 fof/blog 的普通列表卡 BlogOverviewItem 只在 view() 里把标签拼成
 *   `.BlogList-item-category-{tagId}` 这个**没有任何 CSS 规则**的死类名，
 *   既不输出文字也不可点击 —— 于是 /blog 上看起来仍是「一锅粥」。
 *
 * 手法（不改 vendor，走 ItemList 扩展点）：
 *   `extend('ext:fof/blog/forum/components/BlogOverviewItem', 'contentItems', cb)`：
 *   vendor 先把 contentItems() 的返回值（ItemList）交给回调，我们 add 一项 'categories'。
 *   优先级取 70：正好落在 summary(80) 之后、data(60) 之前，
 *   阅读顺序为「标题 → 摘要 → 分类 → 时间/作者/评论」。
 *
 * 三条硬约束（踩坑预防）：
 *   ① 卡片根节点本身是 `<Link>`（即 `<a href="/blog/xxx">`），HTML 不允许嵌套 `<a>`，
 *      所以芯片用 `<span>` + onclick 手动 `m.route.set`，并 stopPropagation，
 *      否则点分类会同时触发文章跳转；
 *   ② 分类口径必须与**文章详情页**一致：vendor 的 `.FoFBlog-Article-Categories`
 *      就是 `article.tags()` 原样全列（含主标签「戒色-文章」）。列表卡早期曾过滤
 *      主标签，结果同一篇文章「列表 1 个分类、详情 2 个分类」，用户反馈「显示不全」
 *      （2026-10-27）。现在只排除 hidden 标签（容器「博客」，id=2），其余全列；
 *   ③ 不引入新请求：`tags` 与 `tags.parent` 已在 BlogListState / Overview.php 的
 *      include 里，`article.tags()` 直接就是完整模型（含 color/slug）。
 */
const BLOG_OVERVIEW_ITEM = 'ext:fof/blog/forum/components/BlogOverviewItem';

/** 只声明用到的 Tag 方法，避免把 flarum/tags 的类型打进 bundle */

/**
 * 芯片文字颜色：按背景亮度在深/浅之间二选一。
 * 本站标签色由后台配置（如「精华」是 #f5a623 的琥珀色），统一用白字会糊；
 * 这里用 sRGB 近似亮度（0.299R+0.587G+0.114B）判断，浅底用深字。
 */
function categoryChipTextColor(hex) {
  if (!hex) return '#ffffff';
  const matched = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!matched) return '#ffffff';
  const int = parseInt(matched[1], 16);
  const r = int >> 16 & 0xff;
  const g = int >> 8 & 0xff;
  const b = int & 0xff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#2b2b2b' : '#ffffff';
}

/**
 * 点击分类芯片 → 跳到博客分类页（/blog/category/<slug>）。
 *
 * 必须 preventDefault + stopPropagation：
 *   · 芯片在 `<a class="BlogList-item">` 内部，不拦的话会冒泡成「打开文章」；
 *   · 用 m.route.set 而非 location.href，保持 SPA 无刷新切换。
 * `Event` 而不是 MouseEvent：同一个处理函数也复用于键盘 Enter（无障碍）。
 */
function goToBlogCategory(event, slug) {
  event.preventDefault();
  event.stopPropagation();
  m.route.set(app.route('blogCategory', {
    slug
  }));
}
function useBlogCategoryChips() {
  extend(BLOG_OVERVIEW_ITEM, 'contentItems', function (items) {
    const article = this.attrs?.article;
    // 复制一份再排序：tags() 返回的是模型上的关系数组，原地 sort 会改掉全局顺序
    const tags = [...(article?.tags?.() || [])];

    // 与详情页口径一致：除 hidden 容器标签外全部展示（含主标签「戒色-文章」）
    const categories = tags.filter(tag => !tag.isHidden());
    if (!categories.length) return;

    // 排序：普通主题标签按后台 position，「精华」永远排最后（它是质量标记，不是主题）
    const isFeatured = tag => tag.slug() === 'jinghua';
    categories.sort((a, b) => Number(isFeatured(a)) - Number(isFeatured(b)) || (a.position() ?? 0) - (b.position() ?? 0));

    /**
     * 全量渲染，不再折叠成「+N」：
     *   本站单篇最多「主标签 + 3 个主题标签 + 精华」= 5 个，卡片宽度足够；
     *   CSS 已 `flex-wrap: wrap`，溢出时自动换行。
     *   折叠等于把分类藏起来，正是用户反馈的「显示不全」。
     */
    items.add('categories', m("div", {
      class: "BlogList-item-categories"
    }, categories.map(tag => m("span", {
      class: `BlogList-item-category BlogList-item-category-${tag.slug()}`,
      style: {
        backgroundColor: tag.color() || undefined,
        color: categoryChipTextColor(tag.color())
      },
      role: "link",
      tabindex: "0",
      title: `查看「${tag.name()}」分类`,
      onclick: event => goToBlogCategory(event, tag.slug()),
      onkeydown: event => {
        if (event.key === 'Enter' || event.key === ' ') goToBlogCategory(event, tag.slug());
      }
    }, tag.name()))), 70);
  });
}

/**
 * 七、侧栏「分类目录」高亮当前分类（2026-10-27 用户要求）。
 *
 * 现象：进入 /blog/category/jiese-wenzhang 后，侧栏「分类目录」里
 *   「博客 / 戒色-文章 / 戒色方法 …」长得一模一样，用户看不出自己正在哪个分类。
 *
 * 根因：vendor 的 `BlogCategories.categoryItem()` 只输出
 *   `BlogSideWidget-item BlogSideWidget-item-{id} [BlogSideWidget-item-child]`，
 *   **没有任何 active 判定**；它自己虽然在 view() 里读了 `m.route.param('slug')`
 *   （用来决定是否展开子分类），却没把这个信息用到样式上。
 *
 * 手法：extend 该组件的 `categoryItem`。Flarum 2.x 的 extend 语义是
 *   「先执行原方法，再把**返回值**交给回调」（见 §六 的踩坑记录），
 *   所以回调里拿到的是已经构造好的 `<Link>` vnode，直接改 attrs 即可：
 *     · href 命中当前路由的 slug → 加 `BlogSideWidget-item-active` 类 + aria-current；
 *     · 从 vendor 自己拼的 `BlogSideWidget-item-{tagId}` 里取出 id，查模型拿到标签色，
 *       写成 CSS 变量 `--jiedh-blog-cat-color`，让「高亮色 = 该分类自己的颜色」。
 *
 * 为什么不用纯 CSS：列表项类名里只有自增 id，没有 slug/URL 信息，
 *   CSS 无法知道当前地址栏是哪个分类（:target 只对锚点有效）。
 */
const BLOG_CATEGORIES = 'ext:fof/blog/forum/components/BlogCategories';

/** 从 vendor 的类名 `BlogSideWidget-item-{id}` 中提取标签 id */
const BLOG_CATEGORY_ID_PATTERN = /BlogSideWidget-item-(\d+)/;
function useActiveBlogCategory() {
  extend(BLOG_CATEGORIES, 'categoryItem', function (vnode) {
    const slug = m.route.param('slug');
    if (!slug || !vnode?.attrs) return;

    // 只在「确实是当前分类页」时高亮：/blog 总览页的 slug 是 undefined，不会误标
    if (vnode.attrs.href !== app.route('blogCategory', {
      slug
    })) return;
    vnode.attrs.className = `${vnode.attrs.className || ''} BlogSideWidget-item-active`.trim();
    vnode.attrs['aria-current'] = 'page';
    const matched = BLOG_CATEGORY_ID_PATTERN.exec(vnode.attrs.className);
    const tag = matched ? app.store.getById('tags', matched[1]) : null;
    const color = tag?.color?.();
    if (color) {
      // 用 CSS 变量把「标签色」传给 less，避免在 JS 里拼死背景色（hover 态还要再算一次亮度）
      vnode.attrs.style = {
        ...(vnode.attrs.style || {}),
        '--jiedh-blog-cat-color': color
      };
    }
  });
}
app.initializers.add('jiedh-media', () => {
  useUploadOnlyBlogCover();
  useBrokenImageFallback();
  removeBlogForumNav();
  useHideMissingBlogCovers();
  useHideMissingBlogCoverOnDetail();
  useBlogCategoryChips();
  useActiveBlogCategory();
});